import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AppButton, AppField, AppTextarea } from "../../../shared/components/ui";
import { useFieldHeader, useFieldJourney } from "../../../shared/components/FieldShell";
import { IconCheck, IconError, IconHourglass, IconSpinner, IconWarning } from "../../../shared/components/icons";
import { runDuplicateCheck, submitDuplicateDecision } from "../api";
import { withEntityQuery } from "../../../shared/navigation/entityPrefill";
import { useOfflineQueue, useOnlineStatus } from "../hooks";
import { enqueue, findLocalLeadByServerId, upsertLocalLead } from "../store";
import type { DuplicateCandidate, DuplicateDecisionRequest, DuplicateDecisionType } from "../types";

/**
 * S3 — Duplicate review decision (handoff TASK-002, s3-duplicate-review-*).
 * Evidence panel + decision options (merge / reject / continue) as
 * secondary-styled options, required audit note, SINGLE primary
 * "Submit Decision". Success → S4 (continue) or lead-closed (merge/reject).
 */

const DECISION_LABELS: Record<DuplicateDecisionType, string> = {
  merge: "Merge records",
  reject: "Reject — not a duplicate",
  continue: "Continue as new",
};

function matchPercent(c: DuplicateCandidate): string | null {
  for (const e of c.evidence) {
    const m = e.match(/name similarity (\d+)%/);
    if (m) return `${m[1]}% match`;
  }
  return null;
}

export function S3DuplicateReview() {
  const { id = "", candidateId = "" } = useParams();
  const navigate = useNavigate();
  const online = useOnlineStatus();
  const { refresh: refreshQueue } = useOfflineQueue(online);
  useFieldHeader("Review duplicate", "Decide: merge / reject / continue");
  useFieldJourney({
    flow: "leads",
    step: 2,
    backTo: `/field/leads/${id}/duplicates`,
  });

  const leadId = Number(id);
  const [decision, setDecision] = useState<DuplicateDecisionType | null>(null);
  const [note, setNote] = useState("");
  const [noteError, setNoteError] = useState<string | null>(null);
  const [decisionError, setDecisionError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [recorded, setRecorded] = useState<{ decision: DuplicateDecisionType; ref: string } | null>(null);
  const [queued, setQueued] = useState(false);

  const query = useQuery({
    queryKey: ["duplicate-check", leadId],
    queryFn: () => runDuplicateCheck(leadId),
    retry: 1,
    staleTime: 60_000,
  });

  const candidate = useMemo(() => {
    if (!query.data) return undefined;
    return query.data.candidates.find((c) => `${c.type}-${c.id}` === candidateId);
  }, [query.data, candidateId]);

  const decisionMutation = useMutation({
    mutationFn: (body: DuplicateDecisionRequest) => submitDuplicateDecision(leadId, body),
    onSuccess: (res, body) => {
      recordLocalDecision(body.decision, body.note);
      if (body.decision === "continue") {
        setRecorded({ decision: body.decision, ref: `AUD-${res.decision.id}` });
      } else {
        navigate(withEntityQuery(`/field/leads/${leadId}/closed?decision=${body.decision}`, {
          customer_id: body.candidate_customer_id,
        }));
      }
    },
    onError: (err) => {
      setSubmitError(err instanceof Error ? err.message : "Failed to record decision — retry");
    },
  });

  function recordLocalDecision(d: DuplicateDecisionType, n: string) {
    const local = findLocalLeadByServerId(leadId);
    if (local) {
      upsertLocalLead({
        ...local,
        status: d === "merge" ? "closed-merge" : d === "reject" ? "closed-reject" : local.status,
        duplicateDecision: { decision: d, note: n, recordedAt: new Date().toISOString() },
      });
    }
  }

  const handleSubmit = () => {
    setSubmitError(null);
    if (!decision) {
      setDecisionError("Select a decision before submitting");
      return;
    }
    setDecisionError(null);
    if (!note.trim()) {
      setNoteError("An audit note is required before submitting");
      return;
    }
    setNoteError(null);
    if (!candidate) return;

    const body: DuplicateDecisionRequest = {
      decision,
      note: note.trim(),
      ...(candidate.type === "customer"
        ? { candidate_customer_id: candidate.id }
        : { candidate_lead_id: candidate.id }),
    };

    if (!online) {
      // Offline variant (s3-duplicate-review-offline-variant): queue the decision.
      enqueue({
        queueId: crypto.randomUUID(),
        kind: "duplicate-decision",
        leadRef: String(leadId),
        payload: body,
        createdAt: new Date().toISOString(),
      });
      recordLocalDecision(decision, body.note);
      refreshQueue();
      setQueued(true);
      return;
    }
    decisionMutation.mutate(body);
  };

  // ── States ────────────────────────────────────────────────────────────────

  if (query.isPending) {
    return (
      <div className="pg-state">
        <span className="state-icon info">
          <IconSpinner size={40} />
        </span>
        <h2>Loading candidate…</h2>
      </div>
    );
  }

  if (query.isError || !candidate) {
    return (
      <div className="pg-state">
        <span className="state-icon error">
          <IconError size={40} />
        </span>
        <h2>Candidate not found</h2>
        <p>
          {query.error instanceof Error
            ? query.error.message
            : "The duplicate candidate could not be loaded."}
        </p>
        <AppButton
          variant="secondary"
          type="button"
          onClick={() =>
            query.isError ? void query.refetch() : navigate(`/field/leads/${leadId}/duplicates`)
          }
        >
          {query.isError ? "Retry" : "Back to results"}
        </AppButton>
      </div>
    );
  }

  if (decisionMutation.isPending) {
    return (
      <div className="pg-state">
        <span className="state-icon info">
          <IconSpinner size={40} />
        </span>
        <h2>Submitting decision…</h2>
        <p>Recording the audit trail</p>
      </div>
    );
  }

  if (queued) {
    return (
      <div className="pg-state">
        <span className="state-icon info">
          <IconHourglass size={40} />
        </span>
        <h2>Decision queued</h2>
        <p>"{decision ? DECISION_LABELS[decision] : ""}" will sync automatically when back online.</p>
        <AppButton
          type="button"
          onClick={() =>
            decision === "continue"
              ? navigate(`/field/leads/${leadId}/kyc`)
              : navigate("/field/leads")
          }
        >
          {decision === "continue" ? "Continue to KYC" : "Back to Leads"}
        </AppButton>
      </div>
    );
  }

  if (recorded) {
    return (
      <div className="pg-state">
        <span className="state-icon success">
          <IconCheck size={40} />
        </span>
        <h2>Decision recorded</h2>
        <p>
          "{DECISION_LABELS[recorded.decision]}" logged with audit note.
          <br />
          Audit ref {recorded.ref}.
        </p>
        <AppButton type="button" onClick={() => navigate(`/field/leads/${leadId}/kyc`)}>
          Continue to KYC
        </AppButton>
      </div>
    );
  }

  const pct = matchPercent(candidate);
  const mergeDisabled = candidate.type !== "customer";

  return (
    <>
      <div className="pg-candidate">
        <div className="cand-head">
          <span className="cand-name">{candidate.name}</span>
          <span className={`pg-badge ${candidate.type === "customer" ? "warning" : "info"}`}>
            <IconWarning size={12} /> {pct ?? "possible"}
          </span>
        </div>
        <span className="cand-evidence">
          {[candidate.phone, candidate.registered_at ? `registered ${candidate.registered_at.slice(0, 10)}` : null]
            .filter(Boolean)
            .join(" · ")}
        </span>
        <span className="cand-evidence">Evidence: {candidate.evidence.join(" · ")}</span>
        <span className="cand-evidence">NIDA {candidate.national_id_masked}</span>
      </div>

      <div className="pg-option-list" role="radiogroup" aria-label="Duplicate decision">
        {(Object.keys(DECISION_LABELS) as DuplicateDecisionType[]).map((d) => (
          <button
            key={d}
            type="button"
            role="radio"
            aria-checked={decision === d}
            className={`pg-option${decision === d ? " selected" : ""}`}
            disabled={d === "merge" && mergeDisabled}
            title={d === "merge" && mergeDisabled ? "Merge is only available against an existing customer" : undefined}
            onClick={() => setDecision(d)}
          >
            {decision === d ? <IconCheck size={14} /> : null}
            {DECISION_LABELS[d]}
          </button>
        ))}
      </div>
      {decisionError ? (
        <div className="pg-inline-error" role="alert">
          <IconWarning size={14} /> {decisionError}
        </div>
      ) : null}

      <AppField label="Audit note (required)" error={noteError ?? undefined}>
        <AppTextarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Reason for this decision…"
          className={noteError ? "pg-textarea error" : undefined}
        />
      </AppField>

      {submitError ? (
        <div className="pg-inline-error" role="alert">
          <IconWarning size={14} /> {submitError}
        </div>
      ) : null}

      <div className="pg-actions">
        <AppButton type="button" onClick={handleSubmit}>
          Submit Decision
        </AppButton>
      </div>
    </>
  );
}
